/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useParams } from 'react-router-dom';
import { t } from 'i18next';
import { Fragment, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Alert, Button, Col, Collapse, Divider, Form, Input, notification, Row, Select, Space, Switch, Tooltip, Typography, InputNumber } from 'antd';
import { CheckCircleOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Misc imports
import { KB_INTEGRATION_PROPERTIES } from '../../constants/main';
import { useCreateIntegrationMutation, useFetchLlmIntegrationApiVersionQuery, useUpdateIntegrationMutation } from '../../store/integrations/integrationsApi';
import { useInitializeSharePointSubscriptionsMutation } from '../../store/knowledgeBases/knowledgeBasesApi';
import { useIntegrationFormConfiguration } from '../../utils/hooks/useIntegrationFormConfiguration';
import { useSharePointConsent } from '../../utils/hooks/useSharePointConsent';
import { useSharePointDrives } from '../../utils/hooks/useSharePointDrives';
import useTheme from '../../utils/hooks/useTheme';
import { INTEGRATIONS, INTEGRATION_TYPE, INTEGRATION_CATEGORY, LLM_PROVIDERS_ALIASES } from '../../constants/integrations';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX } from '../../constants/regex';
import { SHAREPOINT_CLIENT_ID } from '../../constants/env';

// Constant declarations
const { Text } = Typography;

const IntegrationForm = ({
  integration,
  onSuccess,
  onCancel,
  bodyClassName,
  footerClassName
}) => {
  const { orgId } = useParams();
  const { formProps } = useTheme();
  const [form] = Form.useForm();
  const [formSubmissionErrors, setFormSubmissionErrors] = useState();
  const [createIntegration, { isLoading: isCreatingIntegration }] = useCreateIntegrationMutation();
  const [updateIntegration, { isLoading: isUpdatingIntegration }] = useUpdateIntegrationMutation();
  const [initializeSharePointSubscriptions] = useInitializeSharePointSubscriptionsMutation();

  const isSharePoint = integration?.type === INTEGRATION_TYPE.SHAREPOINT_KB;
  const { consentTenantId, handleSharePointConsent } = useSharePointConsent(integration);
  const siteUrlValue = Form.useWatch('siteUrl', form);
  const { driveOptions, isFetchingDrives } = useSharePointDrives(isSharePoint, consentTenantId, siteUrlValue);

  const currentProviderAlias = Object.values(LLM_PROVIDERS_ALIASES).find(provider => integration?.type === provider.type)?.key;
  const { data: apiVersion } = useFetchLlmIntegrationApiVersionQuery({ orgId, providerAlias: currentProviderAlias }, { skip: !currentProviderAlias || integration?.category !== INTEGRATION_CATEGORY.LLM || !orgId });
  const integrationGrantType = Form.useWatch('grantType', form);
  const {
    description: integrationConfigDescription,
    settings: integrationConfigSettings,
    initialValues: integrationConfigInitialValues
  } = useIntegrationFormConfiguration(integration, formSubmissionErrors, integrationGrantType, form, { driveOptions, isFetchingDrives });

  useEffect(() => {
    form.setFieldsValue(integrationConfigInitialValues);
  }, [integrationConfigInitialValues, form]);

  const onSubmit = async (values) => {
    form.validateFields(['name']);
    const valuesToSubmit = {
      ...values,
      type: integration?.type,
      category: integration?.category
    };

    if (values.versionMajor || values.versionMinor) {
      valuesToSubmit.version = values.versionMajor + '.' + values.versionMinor;
    }

    delete valuesToSubmit.versionMajor;
    delete valuesToSubmit.versionMinor;

    if (isSharePoint) {
      valuesToSubmit.tenantId = consentTenantId;
    }

    let response;
    if (integration?.updatedAt) {
      response = await updateIntegration({ orgId, id: integration?.id, data: valuesToSubmit }).unwrap();
    } else {
      response = await createIntegration({ orgId, data: valuesToSubmit }).unwrap();

      if (isSharePoint && response?.id) {
        initializeSharePointSubscriptions({ orgId, integrationId: response.id })
          .unwrap()
          .catch(() => {
            notification.warning({
              message: t('Subscriptions'),
              description: t('Could not initialize webhook subscriptions. Run a full sync to set them up.')
            });
          });
      }
    }

    if (response && onSuccess) {
      // Update operation does not return the integration object, only create
      onSuccess(response);
    }
  };

  const renderMapping = () => {
    switch(integration?.type) {
      case INTEGRATION_TYPE.MICROSOFT_DYNAMICS_CRM:
        return (
          <Fragment>
            <Row
              gutter={16}
              className={`mb-4 ${formProps.flexDirection}`}
            >
              <Col xs={formProps.mainColSpan} className="mb-5">
                <Typography.Paragraph className="mb-0">
                  {t('Contact Mapping')}
                </Typography.Paragraph>

                <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                  {t('Map Dynamics contact to Helvia\'s contact by selecting one of the available properties')}
                </Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan}>
                <Typography.Paragraph className="mb-2">{t('Dynamics OData Property name')}</Typography.Paragraph>

                <div className="position-relative">
                  <Form.Item
                    name={['filterProperty', 'dataPropertyName']}
                    className="mb-2"
                  >
                    <Input size="large" placeholder={t('Set the property name')}/>
                  </Form.Item>

                  {/* Custom design to show a link between the two inputs */}
                  <div
                    className="position-absolute bg-primary"
                    style={{ left: -20, top: 18, width: 20, height: 1 }}
                  />
                  <div
                    className="position-absolute bg-primary"
                    style={{ width: 8, height: 8, left: -4, top: 15, borderRadius: 9999 }}
                  />
                  <div
                    className="position-absolute bg-primary h-100"
                    style={{ left: -20, top: 18, width: 1 }}
                  />
                </div>

                <Typography.Paragraph className="mb-2">{t('Helvia Contact Property')}</Typography.Paragraph>

                <div className="position-relative">
                  <Form.Item
                    name={['filterProperty', 'subscriberPropertyName']}
                    className="mb-2"
                  >
                    <Input size="large" placeholder={t('Match the property name')}/>
                  </Form.Item>

                  {/* Custom design to show a link between the two inputs */}
                  <div
                    className="position-absolute bg-primary"
                    style={{ left: -20, top: 17, width: 20, height: 1 }}
                  />
                  <div
                    className="position-absolute bg-primary"
                    style={{ width: 8, height: 8, left: -4, top: 14, borderRadius: 9999 }}
                  />
                  <div
                    className="position-absolute bg-primary h-100"
                    style={{ left: -20, top: -20, width: 1 }}
                  />
                </div>
              </Col>
            </Row>

            <Divider />

            <Row
              gutter={16}
              className={`mb-4 ${formProps.flexDirection}`}
            >
              <Col xs={formProps.mainColSpan} className="mb-5">
                <Typography.Paragraph className="mb-0">
                  {t('Map Contact Properties')}
                </Typography.Paragraph>

                <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                  {t('Add contact\'s properties to be shown in Helvia\'s console')}
                </Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan}>
                <Form.Item
                  shouldUpdate
                  noStyle
                >
                  {() => (
                    <Form.List
                      name="entityProperties"
                    >
                      {(fields, { add, remove }) => (
                        <Fragment>
                          <div
                            className="m-0 overflow-y-auto"
                            style={{ maxHeight: 420 }}
                          >
                            {
                              fields.map(({ key, name, ...restField }, index) => (
                                <Form.Item key={key}>
                                  <Typography.Paragraph className="mb-2">{t('Property') + ' ' + Number(name + 1)}</Typography.Paragraph>

                                  <Row align="middle" wrap={false} className="mb-2">
                                    <Col flex="auto" className="bg-layout px-3 py-2 border-radius-base">
                                      <Typography.Paragraph className="mb-2">{t('OData Property')}</Typography.Paragraph>

                                      <Form.Item
                                        name={[name, 'dataPropertyPath']}
                                        className="mb-2"
                                      >
                                        <Input autoFocus size="large" placeholder={t('Set the property name')}/>
                                      </Form.Item>

                                      <Typography.Paragraph className="mb-2">{t('Property Display Name')}</Typography.Paragraph>

                                      <Form.Item
                                        name={[name, 'displayName']}
                                        className="mb-2"
                                      >
                                        <Input size="large" placeholder={t('Set the display name')}/>
                                      </Form.Item>

                                      <Typography.Paragraph className="mb-2">{t('Property Type')}</Typography.Paragraph>

                                      <Form.Item
                                        name={[name, 'type']}
                                        className="mb-2"
                                      >
                                        <Select
                                          className="fs-base"
                                          size="large"
                                          placeholder={t('Select the property type')}
                                          options={[
                                            {
                                              label: t('Text'),
                                              value: 'TEXT'
                                            },
                                            {
                                              label: t('Number'),
                                              value: 'NUMBER'
                                            },
                                            {
                                              label: t('Choice'),
                                              value: 'CHOICE'
                                            },
                                            {
                                              label: t('Lookup'),
                                              value: 'LOOKUP'
                                            },
                                            {
                                              label: t('Datetime'),
                                              value: 'DATETIME'
                                            }
                                          ]}
                                          getPopupContainer={triggerNode => triggerNode.parentElement}
                                        />
                                      </Form.Item>

                                      <Button
                                        icon={<DeleteIcon />}
                                        type="text"
                                        className="bordered border-radius-base float-right my-1"
                                        onClick={() => remove(name)}
                                      />
                                    </Col>
                                  </Row>
                                </Form.Item>
                              ))
                            }
                          </div>

                          <Button
                            type="dashed"
                            onClick={() => add()}
                            className="text-muted"
                            icon={<PlusIcon />}
                            size="large"
                            block
                          >
                            {t('Add Property')}
                          </Button>
                        </Fragment>
                      )}
                    </Form.List>
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Fragment>
        );

      case INTEGRATION_TYPE.MICROSOFT_DYNAMICS_KB:
        return (
          <Fragment>
            <Row
              gutter={16}
              className={`mb-4 ${formProps.flexDirection}`}
            >
              <Col xs={formProps.mainColSpan} className="mb-5">
                <Typography.Paragraph className="mb-0">
                  {t('Filter expression')}
                </Typography.Paragraph>

                <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                  {t('Add filter expression to build a query that expresses multiple conditions.')}
                </Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan}>
                <Typography.Paragraph className="mb-2">{t('Filter Expression')}</Typography.Paragraph>

                <Form.Item
                  name={'filterExpression'}
                  className="mb-2"
                >
                  <Input size="large" placeholder={t('Set the filter expression')}/>
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Row
              gutter={16}
              className={`mb-4 ${formProps.flexDirection}`}
            >
              <Col xs={formProps.mainColSpan} className="mb-5">
                <Typography.Paragraph className="mb-0">
                  {t('Map Article Properties')}
                </Typography.Paragraph>

                <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                  {t('Add article\'s properties to be shown in Helvia\'s console')}
                </Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan}>
                <Form.Item
                  shouldUpdate
                  noStyle
                >
                  <Form.List name="entityProperties">
                    {() => (
                      <Fragment>
                        {
                          KB_INTEGRATION_PROPERTIES.map((integrationProperty, index) => {
                            const entityProperties = form.getFieldValue('entityProperties');
                            if (entityProperties && entityProperties.length > 0) {
                              index = entityProperties.findIndex((entityProperty) => entityProperty.domainPropertyName === integrationProperty.name);
                            }

                            return (
                              <div key={integrationProperty.name}>
                                <Typography.Paragraph className="mb-2">{integrationProperty.label}</Typography.Paragraph>

                                <Row align="middle" wrap={false} className="mb-2">
                                  <Col flex="0 1 464px" className="bg-layout border-radius-base px-3 py-2">
                                    {/* Domain Property Name - Hidden */}
                                    <Form.Item
                                      name={[index, 'domainPropertyName']}
                                      initialValue={integrationProperty.name}
                                      hidden
                                    >
                                      <Input placeholder="Domain Property Name" disabled />
                                    </Form.Item>

                                    <Typography.Paragraph className="mb-2">{t('Property path')}</Typography.Paragraph>

                                    {/* Data Property Path */}
                                    <Form.Item
                                      name={[index, 'dataPropertyPath']}
                                      className="mb-1"
                                    >
                                      <Input placeholder="Enter path" />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </div>
                            );
                          })}
                      </Fragment>
                    )}
                  </Form.List>
                </Form.Item>
              </Col>
            </Row>
          </Fragment>
        );

      case INTEGRATION_TYPE.OIDC_AUTHENTICATION:
        return (
          <Fragment>
            <Row
              gutter={16}
              className={`mb-4 ${formProps.flexDirection}`}
            >
              <Col xs={formProps.mainColSpan} className="mb-5">
                <Typography.Text className="mb-0">{t('User Mapping')}</Typography.Text>

                <br />

                <Typography.Text className="fs-sm" italic>
                  {t('Token')}
                </Typography.Text>

                <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                  {t('Describes the Claim keys to extract Email & Fullname which are required for Authentication.')}
                </Typography.Paragraph>
              </Col>

              <Col xs={formProps.secondaryColSpan}>
                <Typography.Paragraph className="mb-2">{t('Email')}</Typography.Paragraph>

                <Form.Item
                  name={'emailKey'}
                  className="mb-2"
                >
                  <Input size="large" placeholder={t('If not set: email is default email key')}/>
                </Form.Item>

                <Typography.Paragraph className="mb-2">{t('Fullname')}</Typography.Paragraph>

                <Form.Item
                  name={'fullNameKey'}
                  className="mb-2"
                >
                  <Input size="large" placeholder={t('Use claim keys as template variables. eg {{name}}')} suffix={(
                    <Tooltip title={t('For example: {{name}} {{middle_name}}{{given_name}}')}>
                      <InfoCircleOutlined className={'text-muted'} />
                    </Tooltip>
                  )} />
                </Form.Item>
              </Col>
            </Row>
          </Fragment>
        );

      default:
        break;
    }
  };

  const integrationSettingsForm = (setting) => {
    const hasComponent = !!(setting.component);
    const isFieldRequired = setting?.rules?.length ? setting?.rules[0]?.required : false;
    return (
      <Fragment key={setting.name}>
        <Typography.Paragraph
          hidden={!hasComponent}
          className={classNames('mb-2', { 'custom-form-label-required': isFieldRequired })}
        >
          {setting.title}
        </Typography.Paragraph>

        {
          hasComponent ? setting.component : setting.select ? (
            <Form.Item
              name={setting.name}
              className="mb-2"
              label={setting.title}
              extra={setting.extra}
              rules={setting.rules || []}
            >
              <Select
                size="large"
                className="fs-base"
                options={setting.select}
                getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
              />
            </Form.Item>
          ) : (
            <Form.Item
              label={setting.title}
              name={setting.name}
              className="mb-2"
              extra={setting.extra}
              rules={setting.rules || []}
            >
              {
                setting.password ? (
                  <Input.Password
                    size="large"
                    placeholder={setting.placeholder || setting.title}
                    autoComplete="new-password" // Prevent autofill
                  />
                ) : setting.number ? (
                  <InputNumber
                    size="large"
                    maxLength={100}
                    controls={false}
                    className="w-100"
                    placeholder={setting.placeholder || setting.title}
                  />
                ) : setting.dropdown ? (
                  <Select options={setting.options} placeholder={setting.placeholder}/>
                ) : (
                  <Input
                    size="large"
                    placeholder={setting.placeholder || setting.title}
                    defaultValue={setting.defaultValue}
                  />
                )
              }
            </Form.Item>
          )}
      </Fragment>
    );
  };

  const integrationType = Object.values(INTEGRATIONS).find((i) => i.type === integration?.type);
  return (
    <Fragment>
      <div className={bodyClassName}>
        <Form
          form={form}
          layout="vertical"
          initialValues={integrationConfigInitialValues}
          onFinish={onSubmit}
          onFinishFailed={(errorInfo) => setFormSubmissionErrors(errorInfo)}
        >
          <Row gutter={4} align="middle">
            <Col className="mt-1">
              {integrationType?.renderTypeIcon({ className: 'fs-lg' })}
            </Col>

            <Col>
              <Space>
                <Typography.Text className="fs-lg" strong>
                  {t(integrationType?.type, { context: 'type' })}
                </Typography.Text>
                |
                <Typography.Text className="fs-lg">
                  {t(integrationType?.category, { context: 'integration category' })}
                </Typography.Text>
              </Space>
            </Col>
            <Col style={{ marginLeft: 'auto', textAlign: 'right' }}>
              <Typography.Paragraph className="fs-sm mb-0" type="secondary">
                {integrationType?.category === INTEGRATION_CATEGORY.LLM ? t(`${apiVersion?.apiVersion ? `Api version: ${apiVersion?.apiVersion}` : ''}`) : ''}
              </Typography.Paragraph>
            </Col>
          </Row>

          <Divider />

          <Row
            gutter={16}
            className={`mb-4 ${formProps.flexDirection}`}
          >
            <Col xs={formProps.mainColSpan} className="mb-5">
              <Typography.Paragraph className="mb-0">
                {t('Integration Details')}
              </Typography.Paragraph>

              <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                {t('Configure the Integration details')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              <Form.Item
                name="enabled"
                className="mb-2"
                valuePropName="checked"
                hidden={true}
              >
                <Switch />
              </Form.Item>

              <Form.Item
                label={t('Name')}
                name="name"
                className="mb-2"
                rules={[{
                  required: true,
                  type: 'string',
                  message: t('Please enter a name')
                }]}
              >
                <Input size="large" maxLength={50} showCount placeholder={t('Set name (max 50 characters)')}/>
              </Form.Item>

              <Form.Item
                label={t('Description')}
                name="description"
                className="mb-2"
              >
                <Input size="large" maxLength={100} showCount placeholder={t('Set a short description (max 100 characters)')}/>
              </Form.Item>

              <Form.Item
                label={t('Tags')}
                name="tags"
                className="mb-2"
              >
                <Select
                  mode="tags"
                  size="large"
                  tokenSeparators={[',']}
                  placeholder={t('Add tags for the integration')}
                  options={
                    integration?.tags?.length > 0 ? integration?.tags.map((tag)=> {
                      return {
                        value: tag,
                        label: tag
                      };
                    }) : null
                  }
                  onInputKeyDown={(e) => {
                    if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                      e.preventDefault();
                      return false;
                    }
                  }}
                  maxTagPlaceholder={(omittedValues) => (
                    <Tooltip
                      placement="bottomLeft"
                      title={
                        <div className="overflow-y-auto overflow-x-hidden scrollbar-custom" style={{ maxHeight: 280, maxWidth: 220 }}>
                          {
                            omittedValues.map((value, index) => (
                              <Text.Paragraph ellipsis key={index} className="fs-base text-muted mb-0">
                                {value.label}

                                <Button
                                  size="small"
                                  type="link"
                                  className="mb-1"
                                  icon={<CloseOutlined className="text-secondary mr-0 fs-sm" />}
                                />
                              </Text.Paragraph>
                            ))
                          }
                        </div>
                      }
                    >
                      +{omittedValues.length}
                    </Tooltip>
                  )}
                  // Fix drop-menu position issues by rendering it in the parent element
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row
            gutter={16}
            className={`mb-4 ${formProps.flexDirection}`}
          >
            <Col xs={formProps.mainColSpan} className="mb-5">
              <Typography.Paragraph className="mb-0">
                {t('Configuration Settings')}
              </Typography.Paragraph>
              <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                {integrationConfigDescription}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              {isSharePoint && (
                <div className="mb-3">
                  {consentTenantId ? (
                    <Alert
                      type="success"
                      showIcon
                      icon={<CheckCircleOutlined />}
                      message={t('Authorization granted')}
                      action={
                        <Tooltip title={t('Your organization has authorized access. You can re-authorize if needed.')}>
                          <Button size="small" onClick={handleSharePointConsent}>
                            {t('Re-authorize')}
                          </Button>
                        </Tooltip>
                      }
                    />
                  ) : (
                    <Alert
                      type="warning"
                      showIcon
                      message={t('Authorization required')}
                      action={
                        <Tooltip title={t('An Azure AD admin must authorize Helvia to access your SharePoint tenant before this integration can sync documents.')}>
                          <Button
                            type="primary"
                            size="small"
                            onClick={handleSharePointConsent}
                            disabled={!SHAREPOINT_CLIENT_ID}
                          >
                            {t('Authorize')}
                          </Button>
                        </Tooltip>
                      }
                    />
                  )}
                </div>
              )}

              {
                integrationConfigSettings.map((setting, index) => {
                  return integrationSettingsForm(setting);
                })
              }
            </Col>
          </Row>

          {
            renderMapping() != null ? (
              <Collapse
                ghost
                defaultActiveKey="mapping"
              >
                <Collapse.Panel
                  className="ant-collapse-p-0"
                  showArrow={false}
                  key="mapping"
                  header={
                    <Divider className="fs-base ant-divider-primary py-3" orientation="left">
                      <Typography.Text strong>
                        <span className="title">
                          {t('Mapping')}
                        </span>
                        <span className="ml-3 icon">
                          <ArrowDownIcon className="fs-base"/>
                        </span>
                      </Typography.Text>
                    </Divider>
                  }
                >
                  {renderMapping()}
                </Collapse.Panel>
              </Collapse>
            ) : null
          }
        </Form>
      </div>

      <div className={footerClassName}>
        <Row justify="end" gutter={8}>
          <Col>
            <Button
              size="large"
              onClick={onCancel}
              disabled={isUpdatingIntegration || isCreatingIntegration}
            >
              {t('Cancel')}
            </Button>
          </Col>

          <Col>
            <Button
              size="large"
              className="d-block ml-auto"
              type="primary"
              onClick={() => form.submit()}
              loading={isUpdatingIntegration || isCreatingIntegration}
              disabled={isSharePoint && !consentTenantId}
            >
              {integration?.updatedAt ? t('Save Changes') : t('Create Integration')}
            </Button>
          </Col>
        </Row>
      </div>
    </Fragment>
  );
};

IntegrationForm.propTypes = {
  integration: PropTypes.object,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string
};

export default IntegrationForm;
